import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { countryManager, localeManager } from "@core/service-container";
import { PostalCodeTemplate, ProfessionalIdentifier } from "@domain/localization/country";
import {
	MedicalProfessionOption,
	useMedicalProfessionOptions,
} from "@ui/pages/referential/use-medical-profession-options";
import { useObservable } from "micro-observables";
import { useCallback, useEffect, useMemo } from "react";

interface UseRegistrationConfigHook {
	isEmpty: boolean;
	isLoading: boolean;
	isError: boolean;
	idTypeOptions: { value: string; label: string }[];
	medicalProfessionOptions: MedicalProfessionOption[];
	selectedIdProps: Omit<ProfessionalIdentifier, "name"> | undefined;
	selectedPostalCodeTemplate: PostalCodeTemplate | undefined;
	refresh: () => void;
}

//TODO refacto by using useCountryConfig
export const useRegistrationCountryUserConfig = (
	selectedIdName?: string,
	setSelectedIdName?: (value: string) => void,
): UseRegistrationConfigHook => {
	const localizeConfig = useObservable(countryManager.countryUserConfig);

	const professionalIdentifiers = localizeConfig?.ptProfessionalIds;
	const selectedPostalCodeTemplate = localizeConfig?.postalCode;
	const medicalProfessions = localizeConfig?.ptMedicalProfessions;
	const countryCode = useObservable(localeManager.countryCode);

	const idTypeOptions = useMemo(
		() => [...(professionalIdentifiers ?? [])].map(([name, details]) => ({ value: name, label: details.label })),
		[professionalIdentifiers],
	);

	const { medicalProfessionOptions } = useMedicalProfessionOptions({ medicalProfessions: medicalProfessions ?? [] });

	const [fetchProfessionalIdentifiers, isLoading, isError] = useAsyncOperation(
		useCallback(async () => await countryManager.getCountryUserConfig(countryCode), [countryCode]),
		{},
	);

	// Set idType's value to be the first available professional identifiers
	useEffect(() => {
		if (
			professionalIdentifiers &&
			professionalIdentifiers.size > 0 &&
			!professionalIdentifiers.has(selectedIdName ?? "")
		) {
			const firstIdType = professionalIdentifiers.keys().next().value as string;
			setSelectedIdName?.(firstIdType);
		}
	}, [professionalIdentifiers, selectedIdName, setSelectedIdName]);

	const selectedIdProps = useMemo(
		() => professionalIdentifiers?.get(selectedIdName ?? ""),
		[selectedIdName, professionalIdentifiers],
	);

	useEffect(() => {
		fetchProfessionalIdentifiers();
	}, [fetchProfessionalIdentifiers]);

	const isEmpty = useMemo(() => professionalIdentifiers?.size === 0, [professionalIdentifiers]);
	const refresh = useCallback(() => {
		void fetchProfessionalIdentifiers();
	}, [fetchProfessionalIdentifiers]);

	return {
		isEmpty,
		isLoading,
		isError,
		idTypeOptions,
		medicalProfessionOptions: medicalProfessionOptions,
		selectedIdProps,
		selectedPostalCodeTemplate,
		refresh,
	};
};
